import { motion } from "framer-motion";
import {
  FileText,
  Scale,
  AlertCircle,
  CheckCircle,
  Gavel,
  Globe,
  CreditCard,
  Ban,
  Phone,
} from "lucide-react";

const sections = [
  {
    id: "overview",
    icon: Globe,
    title: "Overview",
    content: `Welcome to Big Boys Motor Club. By accessing our website, you agree to be bound by these Terms of Service. These terms apply to all visitors, users, and customers who access or use our services. Please read these terms carefully before using our website or making any purchases. If you do not agree with any part of these terms, you should not use our website or services.`,
  },
  {
    id: "eligibility",
    icon: CheckCircle,
    title: "Eligibility",
    content: `By using this website, you confirm that you are at least 18 years of age or the age of majority in your province or state of residence. If you are using this site on behalf of a minor, you confirm that you are their legal guardian and have authority to consent to these terms on their behalf. You may not use our products or services for any illegal or unauthorized purpose.`,
  },
  {
    id: "products",
    icon: Scale,
    title: "Products & Services",
    content: `We have made every effort to display our vehicle inventory as accurately as possible, including colors, images, trim levels, and specifications. However, we cannot guarantee that your device's display will perfectly represent the actual vehicle colors or features. All vehicle descriptions, pricing, and availability are subject to change without notice. We reserve the right to limit quantities, discontinue products, or refuse service to anyone at our sole discretion.`,
  },
  {
    id: "pricing",
    icon: CreditCard,
    title: "Pricing & Payments",
    content: `All prices listed on our website are in Canadian dollars unless otherwise specified. Prices are subject to change without notice. We reserve the right to refuse any order you place with us. When you make a purchase, you agree to provide current, complete, and accurate purchase and account information. You agree to promptly update your account details, including email address and payment information, to ensure we can complete your transactions and contact you as needed.`,
  },
  {
    id: "prohibited",
    icon: Ban,
    title: "Prohibited Activities",
    content: `You are prohibited from using this website for any unlawful purpose, to solicit others to perform unlawful acts, to violate any regulations or laws, to infringe upon intellectual property rights, to harass or discriminate against others, to submit false information, to upload viruses or malicious code, to collect personal information of others without consent, to spam or scrape content, or to interfere with the security features of our website. Violation of these prohibitions may result in immediate termination of your access to our services.`,
  },
  {
    id: "third-party",
    icon: Globe,
    title: "Third-Party Links",
    content: `Our website may contain links to third-party websites that are not owned or controlled by Big Boys Motor Club. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites. By using our website, you acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by or in connection with the use of any third-party content, goods, or services.`,
  },
  {
    id: "warranty",
    icon: AlertCircle,
    title: "Disclaimer of Warranties",
    content: `We do not guarantee that your use of our service will be uninterrupted, timely, secure, or error-free. The service and all products are provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied. This includes warranties of merchantability, fitness for a particular purpose, durability, title, and non-infringement. Your use of our service is at your sole risk.`,
  },
  {
    id: "liability",
    icon: Scale,
    title: "Limitation of Liability",
    content: `In no event shall Big Boys Motor Club, our directors, officers, employees, affiliates, agents, contractors, or licensors be liable for any injury, loss, claim, or damages of any kind arising from your use of our service or products, including direct, indirect, incidental, punitive, special, or consequential damages. This applies even if we have been advised of the possibility of such damages. In jurisdictions that do not allow the exclusion of liability for certain damages, our liability shall be limited to the maximum extent permitted by law.`,
  },
  {
    id: "indemnification",
    icon: Gavel,
    title: "Indemnification",
    content: `You agree to indemnify, defend, and hold harmless Big Boys Motor Club and our affiliates, partners, officers, directors, agents, contractors, licensors, and employees from any claim or demand, including reasonable attorneys' fees, made by any third party due to or arising out of your breach of these Terms of Service, your violation of any law, or your violation of the rights of a third party.`,
  },
  {
    id: "termination",
    icon: Ban,
    title: "Termination",
    content: `These Terms of Service remain in effect until terminated by either party. You may terminate these terms at any time by notifying us that you no longer wish to use our services or by ceasing to use our website. We may also terminate this agreement at any time without notice if we believe you have failed to comply with any term or provision. Upon termination, all obligations and liabilities incurred prior to the termination date shall survive.`,
  },
  {
    id: "governing",
    icon: FileText,
    title: "Governing Law",
    content: `These Terms of Service and any separate agreements whereby we provide you services shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of Ontario.`,
  },
  {
    id: "changes",
    icon: AlertCircle,
    title: "Changes to Terms",
    content: `We reserve the right to update, change, or replace any part of these Terms of Service at our sole discretion. Changes take effect immediately upon posting to this page. It is your responsibility to check this page periodically for changes. Your continued use of or access to the website following the posting of any changes constitutes acceptance of those changes.`,
  },
];

export default function TermsConditions() {
  return (
    <main className="w-full overflow-x-hidden bg-white">
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/20 rounded-full mb-6">
              <FileText className="w-5 h-5 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">
                Legal Information
              </span>
            </div>

            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Terms and <span className="text-brand-gold">Conditions</span>
            </h1>

            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Please review our terms of service carefully. By using our
              website, you agree to be bound by these terms.
            </p>

            <div className="w-24 h-1 bg-gradient-to-r from-brand-red to-brand-gold mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Last Updated Banner */}
      <section className="py-4 bg-gray-50 border-b border-gray-200">
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-600 text-sm">
            Last Updated:{" "}
            <span className="font-semibold text-gray-900">
              February 5, 2026
            </span>
          </p>
        </div> */}
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-full hover:bg-brand-red hover:text-white transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="scroll-mt-32"
                id={section.id}
              >
                <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-red to-brand-gold rounded-xl flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="font-serif text-xl md:text-2xl font-bold text-gray-900">
                      {section.title}
                    </h2>
                  </div>

                  <p className="text-gray-600 leading-relaxed pl-16">
                    {section.content}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Notice */}
      <section className="py-12 bg-brand-red/5 border-y border-brand-red/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-4"
          >
            <div className="w-12 h-12 bg-brand-red rounded-xl flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-bold text-gray-900 mb-3">
                Acceptance of Terms
              </h3>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using our website, you acknowledge that you have
                read, understood, and agree to be bound by these Terms of
                Service. If you do not agree to these terms, please do not use
                our website or services. These terms constitute a legally
                binding agreement between you and Big Boys Motor Club.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl font-bold text-white mb-4">
              Questions About Our Terms?
            </h2>
            <p className="text-gray-400 mb-8">
              If you have any questions or concerns about our Terms of Service,
              please contact us for clarification.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href="tel:+16043588081"
                className="flex items-center gap-3 px-6 py-3 bg-brand-red text-white rounded-full hover:bg-red-700 transition-colors"
              >
                <Phone className="w-5 h-5" />
                (604) 358-8081
              </a>
              <a
                href="mailto:legal@bigboysmotorclub.com"
                className="flex items-center gap-3 px-6 py-3 bg-white/10 text-white border border-white/20 rounded-full hover:bg-white/20 transition-colors"
              >
                <FileText className="w-5 h-5" />
                legal@bigboysmotorclub.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
